/**
 * IsolationSolver y SimulationOverlay del Mapa_Operacional (R2.3, R2.4,
 * R2.5, R2.8).
 *
 * Este módulo resuelve dos cálculos analíticos sobre el Grafo_Entidades,
 * **sin tocar el estado real** de la red:
 *
 *   - **Aislamiento_Valvular** ({@link isolate}/{@link computeIsolation}):
 *     dado un conjunto de Valvulas a cerrar, calcula la zona de red que
 *     queda aislada y el impacto aguas abajo (downstream): conteo de
 *     Clientes, de DMAs y de puntos de Presion afectados (R2.4). El
 *     cálculo es un **recorrido del grafo de red** siguiendo las aristas
 *     `aguas_abajo` y `contiene` de `graph.ts`.
 *
 *   - **Simulacion_Hidraulica** ({@link simulate}): estima la presión y
 *     el flujo aguas abajo de un escenario propuesto **sin mutar el
 *     estado real ni registrar ningún Evento_Operacional de mutación**
 *     (R2.3); etiqueta el resultado como `es_simulacion: true` en una
 *     estructura separada de las capas de estado real (R2.5). Ante un
 *     escenario no completable, **aborta** preservando intacto el estado
 *     real y devuelve un `Result` de error (R2.8).
 *
 * Todas las funciones son **puras y deterministas**: reciben el grafo
 * como parámetro (no acceden a estado global ni hacen fetch), de modo
 * que sean ejercitables tanto en el navegador como en tests Node/Vitest
 * (Properties 7 y 8). No mutan sus argumentos ni emiten efectos.
 *
 * El impacto downstream es esencialmente un recorrido de grafo; Turf.js
 * no es necesario porque {@link ZonaAislada} y {@link ResultadoSimulado}
 * no transportan geometría, solo conteos y estimaciones por entidad.
 */

import type {
  Arista,
  EntidadRef,
  EscenarioSimulacion,
  Id,
  Nodo,
  ResultadoSimulado,
  Result,
  ZonaAislada,
} from "../api/contracts";
import { nodeIdFor, type ProyeccionGrafo } from "./graph";

// ---------------------------------------------------------------------------
// Parámetros del modelo hidráulico simplificado (demo, determinista)
// ---------------------------------------------------------------------------

/** Presión nominal aguas abajo por defecto, en mca, cuando no hay dato. */
export const BASE_PRESION_MCA = 35;

/** Flujo nominal aguas abajo por defecto, en l/s, cuando no hay dato. */
export const BASE_FLUJO_LPS = 10;

/**
 * Aristas que materializan la propagación aguas abajo de un corte
 * valvular: la jerarquía `contiene` (DMA → entidades) y los vínculos
 * explícitos `aguas_abajo` del grafo de red.
 */
const ARISTAS_DOWNSTREAM: ReadonlyArray<string> = ["contiene", "aguas_abajo"];

// ---------------------------------------------------------------------------
// Índice del grafo orientado al recorrido downstream
// ---------------------------------------------------------------------------

interface IndiceIsolation {
  /** Nodos por `node_id`. */
  nodos: Map<Id, Nodo>;
  /** Adyacencia dirigida (origen → destinos) por aristas downstream. */
  downstream: Map<Id, Set<Id>>;
  /** Padres `contiene` de tipo DMA (destino → DMAs que lo contienen). */
  dmasContenedoras: Map<Id, Set<Id>>;
}

function construirIndice(grafo: ProyeccionGrafo): IndiceIsolation {
  const nodos = new Map<Id, Nodo>();
  for (const n of grafo.nodos) nodos.set(n.node_id, n);

  const downstream = new Map<Id, Set<Id>>();
  const dmasContenedoras = new Map<Id, Set<Id>>();

  const agregar = (mapa: Map<Id, Set<Id>>, clave: Id, valor: Id): void => {
    let set = mapa.get(clave);
    if (!set) {
      set = new Set<Id>();
      mapa.set(clave, set);
    }
    set.add(valor);
  };

  for (const e of grafo.aristas as Arista[]) {
    if (ARISTAS_DOWNSTREAM.includes(e.tipo)) {
      agregar(downstream, e.origen_id, e.destino_id);
    }
    // Padre DMA que contiene a un nodo (para subir de la Valvula a su DMA).
    if (e.tipo === "contiene") {
      const origen = nodos.get(e.origen_id);
      if (origen?.tipo === "DMA") {
        agregar(dmasContenedoras, e.destino_id, e.origen_id);
      }
    }
  }

  return { nodos, downstream, dmasContenedoras };
}

// ---------------------------------------------------------------------------
// Recorrido downstream sobre el grafo de red
// ---------------------------------------------------------------------------

/** Impacto downstream de un conjunto de semillas sobre el grafo de red. */
export interface ImpactoDownstream {
  /** Todas las entidades alcanzadas dentro de la zona afectada. */
  zona: EntidadRef[];
  /** DMAs afectadas (subconjunto de `zona`). */
  dmas: EntidadRef[];
  /** Puntos de Presion afectados (subconjunto de `zona`). */
  presion: EntidadRef[];
  /** Conteo de Clientes afectados aguas abajo. */
  clientes_afectados: number;
}

/**
 * Calcula el impacto aguas abajo de un conjunto de entidades semilla
 * (típicamente Valvulas) recorriendo el grafo de red por las aristas
 * `aguas_abajo` y `contiene` (R2.4). Función **pura y determinista**:
 *
 *   1. cada semilla se resuelve a su nodo; semilla inexistente → error
 *      (permite abortar preservando el estado real, R2.8);
 *   2. cada semilla incorpora la(s) DMA(s) que la contienen como raíz del
 *      subárbol afectado (cerrar una Valvula aísla su DMA);
 *   3. desde {semillas ∪ DMAs contenedoras} se hace un BFS dirigido
 *      determinista (vecinos en orden ascendente de `node_id`);
 *   4. se clasifican los nodos alcanzados en DMAs y puntos de Presion y
 *      se cuentan los Clientes afectados.
 *
 * El conteo de Clientes usa los nodos `Cliente` alcanzados si existen; en
 * su defecto, suma el atributo `clientes` de las DMAs afectadas (los
 * JSONs de la fundación modelan los Clientes como agregado de la DMA).
 */
export function downstreamImpact(
  grafo: ProyeccionGrafo,
  semillas: EntidadRef[],
): Result<ImpactoDownstream> {
  if (!semillas || semillas.length === 0) {
    return { ok: false, error: "Se requiere al menos una entidad semilla para el cálculo downstream." };
  }

  const indice = construirIndice(grafo);

  // Raíces del recorrido: cada semilla más su(s) DMA(s) contenedora(s).
  const raices = new Set<Id>();
  for (const ref of semillas) {
    const node_id = nodeIdFor(ref);
    if (!indice.nodos.has(node_id)) {
      return { ok: false, error: `Entidad ${node_id} no encontrada en el grafo de red.` };
    }
    raices.add(node_id);
    const dmas = indice.dmasContenedoras.get(node_id);
    if (dmas) for (const d of dmas) raices.add(d);
  }

  // BFS dirigido determinista por las aristas downstream.
  const visitados = new Set<Id>(raices);
  let frontera: Id[] = [...raices].sort();
  while (frontera.length > 0) {
    const siguiente: Id[] = [];
    for (const actual of frontera) {
      const vecinos = indice.downstream.get(actual);
      if (!vecinos) continue;
      for (const v of [...vecinos].sort()) {
        if (!visitados.has(v)) {
          visitados.add(v);
          siguiente.push(v);
        }
      }
    }
    frontera = siguiente.sort();
  }

  // Clasificación determinista (orden ascendente por node_id).
  const zona: EntidadRef[] = [];
  const dmas: EntidadRef[] = [];
  const presion: EntidadRef[] = [];
  let clientesNodos = 0;
  let clientesPorDma = 0;

  for (const id of [...visitados].sort()) {
    const n = indice.nodos.get(id);
    if (!n) continue;
    zona.push(n.entidad_ref);
    if (n.tipo === "DMA") {
      dmas.push(n.entidad_ref);
      const c = n.attrs?.["clientes"];
      if (typeof c === "number" && Number.isFinite(c)) clientesPorDma += c;
    } else if (n.tipo === "Presion") {
      presion.push(n.entidad_ref);
    } else if (n.tipo === "Cliente") {
      clientesNodos += 1;
    }
  }

  // Si el grafo materializa Clientes como nodos, se cuentan directamente;
  // si no, se usa el agregado `clientes` de las DMAs afectadas.
  const clientes_afectados = clientesNodos > 0 ? clientesNodos : clientesPorDma;

  return { ok: true, value: { zona, dmas, presion, clientes_afectados } };
}

// ---------------------------------------------------------------------------
// Aislamiento_Valvular
// ---------------------------------------------------------------------------

/**
 * Núcleo puro del Aislamiento_Valvular (R2.4). Calcula la zona aislada y
 * el impacto downstream para una o más Valvulas. Determinista y sin
 * efectos. Lista vacía o Valvula inexistente → `Result` de error, lo que
 * permite a la capa de mapa abortar preservando el estado real (R2.8).
 */
export function computeIsolation(grafo: ProyeccionGrafo, valvulas: EntidadRef[]): Result<ZonaAislada> {
  if (!valvulas || valvulas.length === 0) {
    return { ok: false, error: "El Aislamiento_Valvular requiere al menos una Valvula." };
  }

  const impacto = downstreamImpact(grafo, valvulas);
  if (!impacto.ok) return impacto;

  const { zona, dmas, presion, clientes_afectados } = impacto.value;
  return {
    ok: true,
    value: {
      zona,
      clientes_afectados,
      dmas_afectadas: dmas.length,
      puntos_presion_afectados: presion.length,
    },
  };
}

/**
 * Aislamiento_Valvular del Mapa_Operacional (R2.4). Dado el grafo de red
 * y una o más Valvulas a cerrar, devuelve la zona aislada y el impacto
 * downstream (Clientes, DMAs y puntos de Presion afectados). Alias
 * público de {@link computeIsolation}; función pura que recibe el grafo.
 */
export function isolate(grafo: ProyeccionGrafo, valvulas: EntidadRef[]): Result<ZonaAislada> {
  return computeIsolation(grafo, valvulas);
}

// ---------------------------------------------------------------------------
// Simulacion_Hidraulica (SimulationOverlay)
// ---------------------------------------------------------------------------

/**
 * Estimación hidráulica determinista del efecto de un cambio propuesto
 * sobre la presión y el flujo aguas abajo. Modelo simplificado de demo:
 *
 *   - Valvula `cerrada`  → corte total: presión y flujo 0.
 *   - Valvula `parcial`  → presión y flujo a la mitad de lo nominal.
 *   - PRV con `setpoint`  → presión = setpoint; flujo nominal.
 *   - otro caso          → valores nominales (sin efecto significativo).
 */
function estimarDownstream(cambio: Record<string, unknown>): { presion: number; flujo: number } {
  const estado = cambio["estado"];
  if (estado === "cerrada") return { presion: 0, flujo: 0 };
  if (estado === "parcial") return { presion: BASE_PRESION_MCA / 2, flujo: BASE_FLUJO_LPS / 2 };

  const setpoint = cambio["setpoint"];
  if (typeof setpoint === "number" && Number.isFinite(setpoint)) {
    return { presion: setpoint, flujo: BASE_FLUJO_LPS };
  }

  return { presion: BASE_PRESION_MCA, flujo: BASE_FLUJO_LPS };
}

/**
 * Simulacion_Hidraulica sobre un escenario propuesto (R2.3, R2.5, R2.8).
 *
 * Calcula la presión y el flujo estimados aguas abajo de cada cambio
 * propuesto recorriendo el grafo de red, **sin mutar el estado real ni
 * registrar ningún Evento_Operacional de mutación** (la función es pura
 * y no toca el Event_Store, R2.3). El resultado se etiqueta
 * `es_simulacion: true` en una estructura separada de las capas de
 * estado real (R2.5).
 *
 * Ante un escenario no completable —sin cambios, o que referencia una
 * entidad inexistente en el grafo— **aborta** el cálculo, no produce
 * resultado parcial y devuelve un `Result` de error, preservando intacto
 * el estado real (R2.8).
 */
export function simulate(grafo: ProyeccionGrafo, escenario: EscenarioSimulacion): Result<ResultadoSimulado> {
  if (!escenario || !escenario.cambios || escenario.cambios.length === 0) {
    return { ok: false, error: "La simulación no pudo completarse: el escenario no contiene cambios." };
  }

  // Acumula la estimación por entidad downstream (dedupe determinista por
  // node_id). Si un punto recibe varias propagaciones, prevalece la más
  // restrictiva (menor presión) para no subestimar el impacto.
  const porNodo = new Map<Id, { entidad_ref: EntidadRef; presion: number; flujo: number }>();

  for (const { entidad_ref, cambio } of escenario.cambios) {
    const impacto = downstreamImpact(grafo, [entidad_ref]);
    if (!impacto.ok) {
      // Escenario no completable: abortar sin resultado parcial (R2.8).
      return { ok: false, error: `La simulación no pudo completarse: ${impacto.error}` };
    }

    const estimado = estimarDownstream(cambio ?? {});

    // Solo los puntos de Presion afectados portan presión/flujo estimados.
    for (const ref of impacto.value.presion) {
      const key = nodeIdFor(ref);
      const previo = porNodo.get(key);
      if (!previo || estimado.presion < previo.presion) {
        porNodo.set(key, { entidad_ref: ref, presion: estimado.presion, flujo: estimado.flujo });
      }
    }
  }

  const downstream = [...porNodo.entries()]
    .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
    .map(([, v]) => ({ entidad_ref: v.entidad_ref, presion: v.presion, flujo: v.flujo }));

  return { ok: true, value: { es_simulacion: true, downstream } };
}
